import type { ResidentNotificationDto } from "@/api/types";
import { routes } from "@/routes";

const MY_PUP_ORIGINS = new Set([
  "https://login.mypup.app",
  "https://login.staging.mypup.app",
  "https://login.mypup.nl",
]);

interface ResidentNotificationLink {
  href: string;
  external: boolean;
}

function internalLink(href: string): ResidentNotificationLink {
  return { href, external: false };
}

function getMyPupDeepLink(deepLink: string | undefined): ResidentNotificationLink | undefined {
  if (!deepLink) {
    return undefined;
  }

  try {
    const url = new URL(deepLink);

    if (MY_PUP_ORIGINS.has(url.origin)) {
      return { href: url.toString(), external: true };
    }
  } catch {
    return undefined;
  }

  return undefined;
}

/** Resolves supported internal and MyPup destinations, returning `undefined` for non-navigable types. */
export function getResidentNotificationLink(
  notification: ResidentNotificationDto,
  slug: string,
): ResidentNotificationLink | undefined {
  switch (notification.notificationType) {
    case "event":
      return internalLink(routes.events.details({ slug, id: notification.entityId }));
    case "repairRequest":
    case "repairReaction":
    case "repairStatusChange":
    case "adminTicket":
      return internalLink(routes.tickets.details({ slug, id: notification.entityId }));
    case "message":
    case "messageCloseReminder":
      return internalLink(routes.communityFeed.postDetails({ slug, id: notification.entityId }));
    case "survey":
      return internalLink(routes.surveys.respond({ slug, id: notification.entityId }));
    case "poll":
    case "pollV2":
      return internalLink(routes.communityFeed.pollDetails({ slug, id: notification.entityId }));
    case "assetBooking":
      return internalLink(
        notification.subEntityId
          ? routes.bookings.editBooking({ slug, aid: notification.entityId, bid: notification.subEntityId })
          : routes.bookings.assetDetail({ slug, id: notification.entityId }),
      );
    case "integrationMyPupReadyForPickUp":
    case "integrationMyPupReadyForDropOff":
      return (
        getMyPupDeepLink(notification.deepLink) ??
        internalLink(routes.servicePartners.details({ slug, id: notification.entityId }))
      );
    case "integrationBringmeDeposit":
    case "integrationBringmePickUp":
      return internalLink(routes.servicePartners.details({ slug, id: notification.entityId }));
    case "informative":
    case "postDeleted":
    case "commentDeleted":
    default:
      return undefined;
  }
}
